import React, { useEffect, useState } from "react";

import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import {
  LuAlertCircle,
  LuEye,
  LuEyeOff,
  LuKeyRound,
  LuLock,
  LuShieldCheck,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getIsDeletePinRequired,
  getIsDeletePinSet,
  setDeleteSettings,
} from "../../features/appSettings/appSettingsSlice";
import { PUT } from "../../services/axios.service";
import { iconSelectStyles, toastStyles } from "../../static/styles";

interface IFormInput {
  isDeletePinRequired: boolean;
  deletePin: string;
}

const deletePinRequiredOptions = [
  { value: true, label: "હા જરૂરી છે" },
  { value: false, label: "ના જરૂરી નથી" },
];

const DeleteSettingsForm = () => {
  const dispatch = useAppDispatch();
  const isDeletePinRequired = useAppSelector(getIsDeletePinRequired);
  const isDeletePinSet = useAppSelector(getIsDeletePinSet);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPin, setShowPin] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    mode: "onSubmit",
    defaultValues: { isDeletePinRequired: false, deletePin: "" },
  });

  const watchRequired = watch("isDeletePinRequired");

  useEffect(() => {
    reset({ isDeletePinRequired, deletePin: "" });
  }, [isDeletePinRequired, reset]);

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    const pin = data.deletePin ? String(data.deletePin).trim() : "";

    if (pin && !/^\d{6}$/.test(pin)) {
      setError("deletePin", {
        type: "manual",
        message: "ડિલીટ પિન 6 અંકનો હોવો જોઈએ.",
      });
      return;
    }

    if (data.isDeletePinRequired && !isDeletePinSet && !pin) {
      setError("deletePin", {
        type: "manual",
        message: "ડિલીટ પિન સેટ કરો.",
      });
      return;
    }

    setIsLoading(true);

    try {
      await PUT("/v1/admin/settings", {
        isDeletePinRequired: data.isDeletePinRequired,
        ...(pin ? { deletePin: pin } : {}),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            dispatch(
              setDeleteSettings({
                isDeletePinRequired: !!response.data.data.isDeletePinRequired,
                isDeletePinSet: !!response.data.data.isDeletePinSet,
              })
            );

            reset({
              isDeletePinRequired: !!response.data.data.isDeletePinRequired,
              deletePin: "",
            });
            setShowPin(false);

            toast.success(response.data.message, { ...(toastStyles as any) });
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <div className="text-2xl font-bold text-[#113F67] leading-none tracking-tight flex items-center gap-2">
          <LuShieldCheck className="h-6 w-6 text-[#34699A]" />
          ડિલીટ સેટિંગ
        </div>
        <div className="text-sm text-gray-600">
          જ્યારે ચાલુ હોય, ત્યારે દરેક મોડ્યુલમાં ડિલીટ કરતી વખતે 6 અંકનો પિન
          જરૂરી રહેશે.
        </div>
      </div>

      <div className="p-6 pt-0">
        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-[#113F67]">
                ડિલીટ માટે પિન જરૂરી છે <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <LuLock className="absolute left-4 z-[1] top-3.5 h-5 w-5 text-[#34699A]" />
                <Controller
                  name="isDeletePinRequired"
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      value={
                        deletePinRequiredOptions.find(
                          (option) => option.value === field.value
                        ) || null
                      }
                      options={deletePinRequiredOptions}
                      placeholder="પસંદ કરો"
                      isSearchable={false}
                      styles={iconSelectStyles as any}
                      className="react-select-container"
                      classNamePrefix="react-select"
                      onChange={(option: any) => field.onChange(option?.value)}
                    />
                  )}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="deleteSettings-deletePin"
                className="text-sm font-semibold text-[#113F67]"
              >
                ડિલીટ પિન (6 અંક)
                {watchRequired && !isDeletePinSet && (
                  <span className="text-red-500"> *</span>
                )}
              </label>

              <div className="relative">
                <LuKeyRound className="absolute left-4 top-4 h-5 w-5 text-[#34699A]" />
                <input
                  id="deleteSettings-deletePin"
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="new-password"
                  placeholder={
                    isDeletePinSet
                      ? "પિન સેટ થયેલ છે બદલવા માટે નવો પિન દાખલ કરો"
                      : "6 અંકનો પિન દાખલ કરો"
                  }
                  {...register("deletePin")}
                  className={`flex w-full bg-white px-3 py-2 ring-offset-white placeholder:text-[hsl(215_16%_47%)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 pl-12 pr-12 h-12 text-base border-2 rounded-lg transition-all duration-200 ${
                    errors.deletePin
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-200 focus:border-[#113F67] focus:ring-[#113F67]"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-4 top-4 h-5 w-5 text-[#34699A] hover:text-[#113F67] transition-colors"
                >
                  {showPin ? (
                    <LuEyeOff className="h-5 w-5" />
                  ) : (
                    <LuEye className="h-5 w-5" />
                  )}
                </button>
                {errors.deletePin && (
                  <LuAlertCircle className="absolute right-12 top-4 h-5 w-5 text-red-500" />
                )}
              </div>

              {errors.deletePin ? (
                <p className="text-red-500 text-sm flex items-center mt-1">
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  {errors.deletePin.message}
                </p>
              ) : (
                <p className="text-gray-500 text-sm mt-1">
                  {isDeletePinSet
                    ? "પિન સેટ થયેલ છે. ખાલી છોડો તો વર્તમાન પિન યથાવત રહેશે."
                    : "હજુ સુધી કોઈ પિન સેટ થયેલ નથી."}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none h-10 px-6 bg-gradient-to-r from-[#113F67] to-[#34699A] hover:from-[#0d2f4d] hover:to-[#2a547b] text-white font-semibold text-base rounded-lg transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  પિન બદલાઈ રહ્યું છે...
                </div>
              ) : (
                "પિન બદલો"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
export default DeleteSettingsForm;
